import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { ChevronDown } from 'lucide-react'
import * as React from 'react'

import { cn } from '../../utils'

const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('border-b', className)}
    {...props}
  />
))
AccordionItem.displayName = 'AccordionItem'

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    chevronPlacement?: 'left' | 'right'
  }
>(({ className, children, chevronPlacement = 'right', ...props }, ref) => (
  <AccordionPrimitive.Header className='flex'>
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'flex flex-1 items-center justify-between py-4 font-medium text-sm transition-all hover:underline [&[data-state=open]>svg]:rotate-180',
        className
      )}
      {...props}
    >
      {chevronPlacement === 'left' && (
        <ChevronDown className='h-4 w-4 shrink-0 transition-transform duration-200 mr-2' />
      )}
      {children}
      {chevronPlacement === 'right' && (
        <ChevronDown className='h-4 w-4 shrink-0 transition-transform duration-200' />
      )}
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className='overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down'
    {...props}
  >
    <div className={cn('pb-4 pt-0', className)}>{children}</div>
  </AccordionPrimitive.Content>
))

AccordionContent.displayName = AccordionPrimitive.Content.displayName

const SingleItemAccordion = ({ children, className }: { children: React.ReactNode, className?: string }) => {
  const hasAccordionTrigger = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type === AccordionTrigger
  )

  const hasAccordionContent = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type === AccordionContent
  )
  if (!hasAccordionTrigger || !hasAccordionContent) {
    throw new Error('SingleItemAccordion must have an AccordionTrigger and AccordionContent as children')
  }
  return (
    <Accordion type='single' collapsible>
      <AccordionItem value='1' className={cn('border-none', className)}>
        {children}
      </AccordionItem>
    </Accordion>
  )
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger, SingleItemAccordion }
